import { Tick02Icon, Cancel01Icon } from '@hugeicons-pro/core-solid-rounded';
import { Image } from 'expo-image';
import { Link, router } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, Pressable, Platform } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

import { ProfilePicture } from '@/components/domain/common/ProfilePicture';
import { HStack } from '@/components/gluestack/hstack';
import { Skeleton } from '@/components/gluestack/skeleton';
import { Text } from '@/components/gluestack/text';
import { VStack } from '@/components/gluestack/vstack';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { RenderHtml } from '@/components/ui/RenderHtml';
import {
  ImageViewerTriggerWithRef,
  ImageViewerTriggerHandle,
} from '@/components/ui/image-viewer/ImageViewerTrigger';
import { getMemberPath } from '@/constants/routes';
import { useQueryCacheUtils } from '@/hooks/useQueryCacheUtils';
import { useSystemTextTranslation } from '@/hooks/useSystemTextTranslation';
import { useToastMessages } from '@/hooks/useToastMessages';
import { Post } from '@/models/Post';
import { Card } from '~/components/gluestack/card';
import { PostsService } from '~/services/domain/PostsService';

interface NotificationCardProps {
  post?: Post;
}

function NotificationCardComponent({ post }: NotificationCardProps) {
  if (!post) {
    return <NotificationCardSkeleton />;
  }

  return <NotificationCardContent post={post} />;
}

export const NotificationCard = React.memo(NotificationCardComponent);

function NotificationCardContent({ post }: { post: Post }) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { removeCacheItem } = useQueryCacheUtils();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [cardHeight, setCardHeight] = React.useState(0);
  const imageViewerTriggerRef = React.useRef<ImageViewerTriggerHandle | null>(null);
  const opacity = useSharedValue(1);
  const rowHeight = useSharedValue(0);
  const translateX = useSharedValue(0);
  const isDismissing = useSharedValue(false);
  const isHeightCollapsing = useSharedValue(false);

  React.useEffect(() => {
    if (cardHeight > 0) {
      rowHeight.value = cardHeight;
    }
  }, [cardHeight, rowHeight]);

  const removeDismissedNotification = React.useCallback(() => {
    removeCacheItem('notifications', post.Id, 'PostItems');
  }, [post.Id, removeCacheItem]);

  const animatedStyle = useAnimatedStyle(() => {
    if (!isDismissing.value) {
      return { opacity: opacity.value };
    }
    const hasMeasuredHeight = rowHeight.value > 0 || isHeightCollapsing.value;
    return {
      ...(hasMeasuredHeight ? { height: rowHeight.value } : null),
      opacity: opacity.value,
      overflow: 'hidden',
      transform: [{ translateX: translateX.value }],
    };
  });

  const handleLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      const nextHeight = event.nativeEvent.layout.height;
      if (!isProcessing && nextHeight > 0 && Math.abs(nextHeight - cardHeight) > 1) {
        setCardHeight(nextHeight);
      }
    },
    [cardHeight, isProcessing]
  );

  const handlePress = React.useCallback(
    (e: any) => {
      // On web, check if click was on an anchor tag - if so, let it handle navigation
      if (Platform.OS === 'web' && e?.target?.closest?.('a')) {
        return;
      }

      if (
        (post.PostType === 'NotifyNewFollow' || post.PostType === 'NotifyUnlocked') &&
        post.Member?.UserName
      ) {
        router.push(getMemberPath(post.Member) as any);
        return;
      }

      if (post.PhotoId && post.PhotoUrl) {
        imageViewerTriggerRef.current?.open();
        return;
      }
      if (post.PostId) {
        router.push(`/post/${post.PostId}`);
      }
    },
    [post]
  );

  const systemText = useSystemTextTranslation(post);
  const relatedContentText = useSystemTextTranslation(post, true);

  // For NotifyNewComment with RelatedPostType, show the formatted related post text as content
  const contentText =
    post.PostType === 'NotifyNewComment' && post.RelatedPostType ? relatedContentText : null;

  const handleAcceptUnlock = React.useCallback(async () => {
    if (isProcessing || !post) return;
    try {
      setIsProcessing(true);
      await PostsService.acceptPhotoUnlock(post.Id);
      removeCacheItem('notifications', post.Id, 'PostItems');
      showSuccessToast(t('notifications.unlockAcceptSuccess'), undefined, 'bottom');
    } catch (error) {
      showErrorToast(t('notifications.unlockAcceptError'), error, 'bottom');
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, post, removeCacheItem, showErrorToast, showSuccessToast, t]);

  const handleRejectUnlock = React.useCallback(async () => {
    if (isProcessing || !post) return;
    try {
      setIsProcessing(true);
      await PostsService.rejectPhotoUnlock(post.Id);
      removeCacheItem('notifications', post.Id, 'PostItems');
      showSuccessToast(t('notifications.unlockRejectSuccess'), undefined, 'bottom');
    } catch (error) {
      showErrorToast(t('notifications.unlockRejectError'), error, 'bottom');
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, post, removeCacheItem, showErrorToast, showSuccessToast, t]);

  const handleDismiss = React.useCallback(async () => {
    if (isProcessing || !post) return;
    try {
      setIsProcessing(true);
      await PostsService.dismissNotification(post.Id);

      isDismissing.value = true;
      opacity.value = withTiming(0, { duration: 160 }, (finished) => {
        if (finished && cardHeight === 0) {
          scheduleOnRN(removeDismissedNotification);
        }
      });
      translateX.value = withTiming(-16, { duration: 160 });
      if (cardHeight > 0) {
        isHeightCollapsing.value = true;
        rowHeight.value = cardHeight;
        rowHeight.value = withTiming(0, { duration: 220 }, (finished) => {
          if (finished) {
            scheduleOnRN(removeDismissedNotification);
          }
        });
      }
    } catch (error) {
      showErrorToast(t('notifications.dismissError'), error, 'bottom');
      setIsProcessing(false);
    } finally {
      if (cardHeight === 0) {
        setIsProcessing(false);
      }
    }
  }, [
    cardHeight,
    isDismissing,
    isHeightCollapsing,
    isProcessing,
    opacity,
    post,
    removeDismissedNotification,
    rowHeight,
    showErrorToast,
    t,
    translateX,
  ]);

  return (
    <Animated.View style={animatedStyle}>
      <Card size="md" onLayout={handleLayout}>
        <HStack className="items-start gap-2">
          <Pressable onPress={handlePress} className="flex-1">
            <VStack className="flex-1">
              <HStack className="flex-1 items-start">
                <ProfilePicture member={post.Member} showTooltip />

                <VStack className="ml-4 flex-1 items-start gap-1">
                  <Text>
                    <Link
                      href={getMemberPath(post.Member)}
                      className="font-body text-base font-bold text-typography-900 no-underline web:hover:underline">
                      {post.Member.UserName}
                    </Link>{' '}
                    {post.NotificationOthersCount > 0 && (
                      <Text className="font-normal">
                        {t('notifications.andOthers', { count: post.NotificationOthersCount })}{' '}
                      </Text>
                    )}
                    {systemText && <Text className="font-normal">{systemText}</Text>}
                  </Text>

                  <RelativeTime date={post.DateCreated} className="text-sm text-typography-500" />
                </VStack>
              </HStack>

              {/* Notification Content */}
              {(post.HasText || contentText) && (
                <VStack className="pt-2">
                  <RenderHtml
                    html={contentText || post.TextNotEdited}
                    characterLimit={100}
                    noReadMoreButton
                    size="sm"
                    removeLineBreaks
                    className="italic"
                  />
                </VStack>
              )}

              {/* Post Image */}
              {post.PhotoIconUrl && post.PhotoUrl && (
                <VStack className="mt-4">
                  <HStack className="items-center gap-4">
                    <ImageViewerTriggerWithRef
                      ref={imageViewerTriggerRef}
                      uri={post.PhotoUrl}
                      photoId={post.PhotoId ?? undefined}
                      allowImageDownload={!!post.Member?.IsEvent}>
                      <Image
                        source={{ uri: post.PhotoIconUrl }}
                        className="h-32 w-32 rounded-lg"
                        contentFit="cover"
                        recyclingKey={post.PhotoIconUrl}
                      />
                    </ImageViewerTriggerWithRef>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </Pressable>

          <ButtonWrapper
            icon={Cancel01Icon}
            action="neutral"
            size="sm"
            onPress={handleDismiss}
            isDisabled={isProcessing}
            accessibilityLabel={t('notifications.dismiss')}
          />
        </HStack>

        {post.PostType === 'NotifyUnlockRequest' && (
          <HStack className="mt-4 items-center gap-3">
            <ButtonWrapper
              icon={Tick02Icon}
              text={t('common.accept')}
              action="positive"
              size="sm"
              onPress={handleAcceptUnlock}
              isDisabled={isProcessing}
            />
            <ButtonWrapper
              icon={Cancel01Icon}
              text={t('common.reject')}
              action="negative"
              size="sm"
              onPress={handleRejectUnlock}
              isDisabled={isProcessing}
            />
          </HStack>
        )}
      </Card>
    </Animated.View>
  );
}

function NotificationCardSkeleton() {
  return (
    <Card size="md">
      <HStack className="items-center">
        <Skeleton className="h-12 w-12 rounded-full" />

        <VStack className="ml-4 flex-1 gap-2">
          <Skeleton className="h-5 w-36 rounded-md" />
          <Skeleton className="h-4 w-24 rounded-md" />
        </VStack>
      </HStack>

      {/* Post Content Skeleton */}
      <VStack className="py-4">
        <Skeleton className="mb-2 h-4 w-full rounded-md" />
        <Skeleton className="mb-2 h-4 w-full rounded-md" />
        <Skeleton className="h-4 w-3/4 rounded-md" />
      </VStack>
    </Card>
  );
}
